import React, { useState } from 'react'
import { User, Settings, Bell } from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/contexts/Auth0Context'
import { useToast } from '@/contexts/ToastContext'
import ProfileEditForm from '@/components/profile/ProfileEditForm'
import ChangePasswordModal from '@/components/profile/ChangePasswordModal'
import NotificationPreferencesModal from '@/components/profile/NotificationPreferencesModal'
import { profileService, ProfileUpdateData } from '@/services/profileService'
import { NotificationPreferences } from '@/types/user'

const ProfilePage: React.FC = () => {
  const { user, updateUser } = useAuth()
  const { showToast } = useToast()
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false)
  const [isSavingPreferences, setIsSavingPreferences] = useState(false)

  const handleSaveProfile = async (data: ProfileUpdateData, pictureFile?: File) => {
    if (!user) return

    setIsLoading(true)
    try {
      let updatedUser = await profileService.updateProfile(data)

      if (pictureFile) {
        const { profilePicture } = await profileService.uploadProfilePicture(pictureFile)
        updatedUser = { ...updatedUser, profile: { ...updatedUser.profile, profilePicture, hasUploadedPicture: true } }
      }

      updateUser(updatedUser)
      setIsEditing(false)
      showToast({
        type: 'success',
        message: 'Profile Updated',
        description: 'Your profile has been successfully updated.',
      })
    } catch (error) {
      showToast({
        type: 'error',
        message: 'Update Failed',
        description: error instanceof Error ? error.message : 'Failed to update profile. Please try again.',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
  }

  const handleRemovePicture = async () => {
    if (!user) return undefined

    const { profilePicture } = await profileService.deleteProfilePicture()
    updateUser({ ...user, profile: { ...user.profile, profilePicture, hasUploadedPicture: false } })
    showToast({
      type: 'success',
      message: 'Picture Removed',
      description: 'Your profile picture has been removed.',
    })

    return profilePicture
  }

  const handleChangePassword = async () => {
    setIsChangingPassword(true)
    try {
      await profileService.changePassword()
      setIsPasswordModalOpen(false)
      showToast({
        type: 'success',
        message: 'Reset Email Sent',
        description: 'Check your inbox for a link to reset your password.',
      })
    } catch (error) {
      showToast({
        type: 'error',
        message: 'Request Failed',
        description: error instanceof Error ? error.message : 'Failed to send reset email. Please try again.',
      })
    } finally {
      setIsChangingPassword(false)
    }
  }

  const handleSaveNotificationPreferences = async (preferences: NotificationPreferences) => {
    setIsSavingPreferences(true)
    try {
      const updatedUser = await profileService.updateProfile({ notificationPreferences: preferences })
      updateUser(updatedUser)
      setIsNotificationsModalOpen(false)
      showToast({
        type: 'success',
        message: 'Preferences Updated',
        description: 'Your notification preferences have been saved.',
      })
    } catch (error) {
      showToast({
        type: 'error',
        message: 'Update Failed',
        description: error instanceof Error ? error.message : 'Failed to save preferences. Please try again.',
      })
    } finally {
      setIsSavingPreferences(false)
    }
  }

  if (!user) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
          <p className="mt-2 text-gray-600">
            Manage your account settings and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Overview */}
          <div className="lg:col-span-2">
            {isEditing ? (
              <ProfileEditForm
                user={user}
                onSave={handleSaveProfile}
                onRemovePicture={handleRemovePicture}
                onCancel={handleCancelEdit}
                isLoading={isLoading}
              />
            ) : (
            <Card>
              <CardHeader>
                <h2 className="text-lg font-semibold text-gray-900">
                  Profile Information
                </h2>
              </CardHeader>
              <CardBody>
                <div className="flex items-center space-x-6 mb-6">
                  <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden">
                    {user?.profile?.profilePicture ? (
                      <img
                        src={user.profile.profilePicture}
                        alt={`${user.firstName} ${user.lastName}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-10 h-10 text-primary-600" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {user?.firstName} {user?.lastName}
                    </h3>
                    <p className="text-gray-500 capitalize">{user?.role}</p>
                    <p className="text-gray-500">{user?.email}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bio
                    </label>
                    <p className="text-gray-600 text-sm">
                      {user?.profile?.bio || 'No bio added yet.'}
                    </p>
                  </div>

                  {user?.role === 'mentor' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expertise Areas
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {user?.profile?.expertise?.length > 0 ? (
                          user.profile.expertise.map((skill, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-primary-100 text-primary-800 text-sm rounded-full"
                            >
                              {skill}
                            </span>
                          ))
                        ) : (
                          <p className="text-gray-500 text-sm">No expertise areas added yet.</p>
                        )}
                      </div>
                    </div>
                  )}

                  {user?.role === 'mentee' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Interests
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {user?.profile?.interests?.length > 0 ? (
                            user.profile.interests.map((interest, index) => (
                              <span
                                key={index}
                                className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                              >
                                {interest}
                              </span>
                            ))
                          ) : (
                            <p className="text-gray-500 text-sm">No interests added yet.</p>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Learning Goals
                        </label>
                        <div className="space-y-1">
                          {user?.profile?.goals?.length > 0 ? (
                            user.profile.goals.map((goal, index) => (
                              <p key={index} className="text-gray-600 text-sm">
                                • {goal}
                              </p>
                            ))
                          ) : (
                            <p className="text-gray-500 text-sm">No learning goals added yet.</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <Button 
                    variant="primary"
                    onClick={() => setIsEditing(true)}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                </div>
              </CardBody>
            </Card>
            )}
          </div>

          {/* Account Settings */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-gray-900">
                  Account Settings
                </h3>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setIsNotificationsModalOpen(true)}
                  >
                    <Bell className="w-4 h-4 mr-2" />
                    Notifications
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-red-600 hover:text-red-700"
                    onClick={() => setIsPasswordModalOpen(true)}
                  >
                    Change Password
                  </Button>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-gray-900">
                  Account Status
                </h3>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Email Verified</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      user?.isVerified 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {user?.isVerified ? 'Verified' : 'Pending'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Account Status</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      user?.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user?.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Member Since</span>
                    <span className="text-sm text-gray-900">
                      {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>

      <ChangePasswordModal
        email={user.email}
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onConfirm={handleChangePassword}
        isSubmitting={isChangingPassword}
      />

      <NotificationPreferencesModal
        preferences={user.notificationPreferences}
        isOpen={isNotificationsModalOpen}
        onClose={() => setIsNotificationsModalOpen(false)}
        onSave={handleSaveNotificationPreferences}
        isSubmitting={isSavingPreferences}
      />
    </div>
  )
}

export default ProfilePage